import { useDispatch, useSelector } from "react-redux";
import selectors from "selectors";
import { useHistory } from "react-router-dom";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { TextField, Button } from "@mui/material";
import { registerAccount } from "actions/auth";
import { Spinner } from "components/ui";
import { isValidEmail } from "utils/validation";
import { translateUsingBrowser } from "utils/misc";
import { PhoneNumberInput, DateInputField } from "components/form";
import useDocumentTitle from "hooks/use-document-title";

const RegisterValidationScheme = Yup.object().shape({
  first_name: Yup.string()
    .max(100, translateUsingBrowser("tooManyCharacters"))
    .required(translateUsingBrowser("enterFirstName")),
  last_name: Yup.string()
    .max(100, translateUsingBrowser("tooManyCharacters"))
    .required(translateUsingBrowser("enterLastName")),
  email_adress: Yup.string()
    .required(translateUsingBrowser("pleaseEnterEmail"))
    .test("valid-email", translateUsingBrowser("enterValidEmail"), value =>
      isValidEmail(value)
    ),
  phone_number: Yup.string().required(
    translateUsingBrowser("enterPhoneNumber")
  ),
  dob: Yup.string().required(translateUsingBrowser("pleaseEnterDoB"))
});

export const Register = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const registrationError = useSelector(selectors.getUserRegistrationError);

  useDocumentTitle(translateUsingBrowser("register"));

  const getFieldError = (field, errors, touched) => {
    return touched[field] ? errors[field] : "";
  };

  return (
    <div className="login-screen" role="main">
      <div className="login-background" aria-hidden="true">
        <div className="white-background"></div>
        <div className="color-background"></div>
      </div>
      
      <header className="login-screen-header">
        <div className="login-logo" role="img" aria-label="My Sleep Coach Powered by WellSky" />
      </header>

      <div className="login-container">
        <div className="login-form-card" role="form">
          <h1 className="login-card-header" id="register-title">
            {translateUsingBrowser("register")}
          </h1>
          <Formik
            initialValues={{
              first_name: "",
              last_name: "",
              email_adress: "",
              phone_number: "",
              dob: ""
            }}
            validationSchema={RegisterValidationScheme}
            onSubmit={async values => {
              await dispatch(registerAccount(values));
            }}
          >
            {({
              isSubmitting,
              handleChange,
              handleSubmit,
              errors,
              isValid,
              touched,
              handleBlur,
              values
            }) => (
              <form className="login-form" onSubmit={handleSubmit} aria-labelledby="register-title">
                <div className="form-field">
                  <TextField
                    id="first_name"
                    label={translateUsingBrowser("firstName")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                    name="first_name"
                    error={!!getFieldError("first_name", errors, touched)}
                    helperText={getFieldError("first_name", errors, touched)}
                    type="text"
                    autoComplete="given-name"
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                </div>
                <div className="form-field">
                  <TextField
                    id="last_name"
                    label={translateUsingBrowser("lastName")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                    name="last_name"
                    error={!!getFieldError("last_name", errors, touched)}
                    helperText={getFieldError("last_name", errors, touched)}
                    type="text"
                    autoComplete="family-name"
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                </div>
                <div className="form-field">
                  <TextField
                    id="email_adress"
                    label={translateUsingBrowser("email")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email_adress}
                    name="email_adress"
                    error={!!getFieldError("email_adress", errors, touched)}
                    helperText={getFieldError("email_adress", errors, touched)}
                    type="email"
                    autoComplete="email"
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                </div>
                <div className="form-field">
                  <PhoneNumberInput
                    id="phone"
                    label={translateUsingBrowser("phoneNumber")}
                    name="phone_number"
                    type="text"
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                </div>
                <div className="form-field">
                  <Field
                    name="dob"
                    component={DateInputField}
                    label={translateUsingBrowser("dateOfBirth")}
                  />
                </div>

                <div className="login-info" role="status" aria-live="polite">
                  {registrationError && (
                    <div className="login-error" role="alert">
                      {registrationError}
                    </div>
                  )}
                </div>
                {isSubmitting ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 120
                    }}
                    role="status"
                    aria-label="Creating account"
                  >
                    <Spinner />
                  </div>
                ) : (
                  <div className="login-form-actions">
                    <Button
                      variant="contained"
                      color="primary"
                      id="register-btn"
                      type="submit"
                      disabled={!isValid}
                    >
                      {translateUsingBrowser("registerAccount")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      type="button"
                      onClick={() => {
                        history.push("/login");
                      }}
                    >
                      {translateUsingBrowser("backToLogin")}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Register;
